<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative  z-[100]" @close="handleClose">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/30 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel 
              class="w-full max-w-4xl max-h-[50rem] overflow-y-auto transform overflow-hidden rounded-2xl bg-white dark:bg-secondary-800 p-6 shadow-xl transition-all"
            >
              <!-- Header -->
              <div class="relative">
                <div class="absolute -top-6 -right-6  -left-6 h-36 bg-gradient-to-br from-primary-400 to-primary-600 dark:from-primary-600 dark:to-primary-800">
                  <div class="absolute inset-0 bg-[url('/src/assets/patterns/circuit-board.svg')] opacity-10"></div>
                </div>
                
                <div class="relative text-center mt-8 mb-6">
                  <h3 class="text-2xl font-bold text-white">
                    {{ t(update.title) }}
                  </h3>
                  <p class="mt-2 text-primary-100">
                    {{ t(update.description) }}
                  </p>
                  <div class="mt-2 text-sm text-primary-200">
                    {{ t('common.version') }} {{ update.version }}
                  </div>
                </div>
              </div>

              <!-- Features -->
              <div class="mt-8 space-y-6">
                <TransitionGroup
                  enter-active-class="transition duration-300 ease-out delay-[var(--delay)]"
                  enter-from-class="opacity-0 translate-y-4"
                  enter-to-class="opacity-100 translate-y-0"
                  leave-active-class="transition duration-200 ease-in"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <div
                    v-for="(feature, index) in update.features"
                    :key="feature.id"
                    :style="{ '--delay': `${index * 100}ms` }"
                    class="flex items-start gap-4 p-4 bg-secondary-50 dark:bg-secondary-900/50 rounded-xl"
                  >
                    <div 
                      class="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full text-2xl"
                      :class="{
                        'bg-green-100 dark:bg-green-900/40': feature.type === 'new',
                        'bg-blue-100 dark:bg-blue-900/40': feature.type === 'improvement',
                        'bg-yellow-100 dark:bg-yellow-900/40': feature.type === 'fix'
                      }"
                    >
                      {{ feature.icon }}
                    </div>
                    <div>
                      <h4 class="text-lg font-medium text-secondary-900 dark:text-white">
                        {{ t(feature.title) }}
                      </h4>
                      <p class="mt-1 text-secondary-600 dark:text-secondary-400">
                        {{ t(feature.description) }}
                      </p>
                    </div>
                  </div>
                </TransitionGroup>
              </div>

              <!-- Footer -->
              <div class="mt-8 flex justify-end">
                <button
                  type="button"
                  class="btn-primary"
                  @click="handleClose"
                >
                  {{ t('common.understood') }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import confetti from 'canvas-confetti'

interface Feature {
  id: string
  title: string
  description: string
  icon: string
  type: 'new' | 'improvement' | 'fix'
}

interface AppUpdate {
  id: string
  version: string
  title: string
  description: string
  releaseDate: Date
  features: Feature[]
  priority: 'low' | 'medium' | 'high'
  showConfetti: boolean
}

const { t } = useI18n()

const props = defineProps<{
  isOpen: boolean
  update: AppUpdate
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const handleClose = () => {
  emit('close')
}

const showConfetti = () => {
  const count = 200
  const defaults = {
    origin: { y: 0.7 }
  }

  function fire(particleRatio: number, opts: any) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio)
    })
  }

  fire(0.25, {
    spread: 26,
    startVelocity: 55,
  })

  fire(0.2, {
    spread: 60,
  })

  fire(0.35, {
    spread: 100,
    decay: 0.91,
    scalar: 0.8
  })

  fire(0.1, {
    spread: 120,
    startVelocity: 25,
    decay: 0.92,
    scalar: 1.2
  })

  fire(0.1, {
    spread: 120,
    startVelocity: 45,
  })
}

watch(() => props.isOpen, (newVal) => {
  if (newVal && props.update.showConfetti) {
    setTimeout(showConfetti, 400)
  }
})

onMounted(() => {
  if (props.isOpen && props.update.showConfetti) {
    setTimeout(showConfetti, 400)
  }
})
</script>
